import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { useAPIAuthenticatedService } from '../../helpers/APICallsAuthenticated';
import { CacheKeys, OfferWithScope_UserWithoutId } from '../../helpers/DataStructures';
import CreateReferralForm from '../../forms/CreateReferralForm';
import { DisplayOfferInGridHeader } from '../../components/DisplayOfferInGrid';
import DisplayOfferInGridWrapper from '../../components/DisplayOfferInGridWrapper';
import InHouseDialog from '../../components/InHouseDialog';

const PROFESSIONS = [
  'estate agent',
  'surveyor',
  'mortgage broker',
  'conveyancer',
  'epc assessor',
  'removal and storage',
  'damp and timber',
  'asbestos surveyors',
  'drain engineers',
  'house cleaners',
  'electrician',
  'plumber',
  'property maintenance',
] as const;

const PROFESSION_LABELS: Record<(typeof PROFESSIONS)[number], string> = {
  'estate agent': 'Estate Agents',
  surveyor: 'Surveyors',
  'mortgage broker': 'Mortgage Brokers',
  conveyancer: 'Conveyancers',
  'epc assessor': 'EPC Assessor',
  'removal and storage': 'Removal and Storage',
  'damp and timber': 'Damp and Timber',
  'asbestos surveyors': 'Asbestos Surveyors',
  'drain engineers': 'Drain Engineers',
  'house cleaners': 'House Cleaners',
  electrician: 'Electricians',
  plumber: 'Plumbers',
  'property maintenance': 'Property Maintenance',
};

type OfferSortKey = 'user.givenName' | 'location' | 'offeringFixedCashAmount';

const ReferralsAppMakeReferral: React.FC = () => {
  const [selectedProfession, setSelectedProfession] = useState<string>('none');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<OfferWithScope_UserWithoutId | null>(null);
  const lifecyclePrefix = 'lifecycle-debug';

  useEffect(() => {
    console.log(`[${lifecyclePrefix}] ReferralsAppMakeReferral mount`);

    return () => {
      console.log(`[${lifecyclePrefix}] ReferralsAppMakeReferral unmount`);
    };
  }, []);

  const handleChangeProfession = (newValue: string) => {
    setSelectedProfession(newValue === selectedProfession ? 'none' : newValue);
  };

  const handleOpenDialog = (offer: OfferWithScope_UserWithoutId) => {
    setSelectedOffer(offer);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  return (
    <>
      <CardContent sx={{ padding: '0px' }}>
        {PROFESSIONS.map((profession) => (
          <Accordion
            key={profession}
            expanded={selectedProfession === profession}
            onChange={() => handleChangeProfession(profession)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{PROFESSION_LABELS[profession]}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: { xs: '1px', md: '5px' } }}>
              {selectedProfession === profession && (
                <ProfessionOffersTable
                  onOpenDialog={handleOpenDialog}
                  selectedProfession={profession}
                />
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </CardContent>

      {selectedOffer && (
        <InHouseDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          title="Make a Referral"
          closeLabel="Cancel"
        >
          <Box sx={{ textAlign: 'center' }}>
            {selectedOffer.offeringFixedCashAmount > 0 && (
              <>
                <Typography variant="h6">
                  Send a Referral to {selectedOffer.user.givenName} {selectedOffer.user.familyName}
                </Typography>
                {selectedOffer.location === 'Not Specified' ? null : (
                  <Typography variant="h6">in {selectedOffer.location}</Typography>
                )}
                <Typography variant="bigPrimary">
                  &pound;{selectedOffer.offeringFixedCashAmount}
                </Typography>
              </>
            )}

            {selectedOffer.offeringFixedCashAmount === 0 && (
              <Typography variant="h6">
                Send a Referral to {selectedOffer.user.givenName} {selectedOffer.user.familyName}{' '}
                {selectedOffer.location === 'Not Specified' ? '' : `in ${selectedOffer.location}`}
              </Typography>
            )}

            {selectedOffer.theOffer && (
              <Typography variant="body2" mb={2}>
                {selectedOffer.theOffer}
              </Typography>
            )}

            <CreateReferralForm offerId={selectedOffer.id} closeDialog={handleCloseDialog} />
          </Box>
        </InHouseDialog>
      )}
    </>
  );
};

interface PropsProfessionOffersTable {
  selectedProfession: string;
  onOpenDialog: (offer: OfferWithScope_UserWithoutId) => void;
}

const ProfessionOffersTable: React.FC<PropsProfessionOffersTable> = ({
  selectedProfession,
  onOpenDialog,
}) => {
  const apiAuthenticatedService = useAPIAuthenticatedService();
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<OfferSortKey>('offeringFixedCashAmount');
  const lifecyclePrefix = 'lifecycle-debug';

  useEffect(() => {
    console.log(`[${lifecyclePrefix}] ProfessionOffersTable mount`, { selectedProfession });

    return () => {
      console.log(`[${lifecyclePrefix}] ProfessionOffersTable unmount`, { selectedProfession });
    };
  }, []);

  const handleSortRequest = (property: OfferSortKey) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const descendingComparator = (
    a: OfferWithScope_UserWithoutId,
    b: OfferWithScope_UserWithoutId,
    sortKey: OfferSortKey
  ) => {
    if (sortKey === 'user.givenName') {
      if (b.user.givenName < a.user.givenName) {
        return -1;
      }
      if (b.user.givenName > a.user.givenName) {
        return 1;
      }
    } else if (sortKey === 'location') {
      if (b.location < a.location) {
        return -1;
      }
      if (b.location > a.location) {
        return 1;
      }
    } else if (sortKey === 'offeringFixedCashAmount') {
      if (b.offeringFixedCashAmount < a.offeringFixedCashAmount) {
        return -1;
      }
      if (b.offeringFixedCashAmount > a.offeringFixedCashAmount) {
        return 1;
      }
    }

    return 0;
  };

  const getComparator = (sortDirection: 'asc' | 'desc', sortKey: OfferSortKey) =>
    sortDirection === 'desc'
      ? (a: OfferWithScope_UserWithoutId, b: OfferWithScope_UserWithoutId) =>
          descendingComparator(a, b, sortKey)
      : (a: OfferWithScope_UserWithoutId, b: OfferWithScope_UserWithoutId) =>
          -descendingComparator(a, b, sortKey);

  const { isLoading, isError, data } = useQuery(
    [CacheKeys.OFFERS_BY_PROFESSION_AUTHENTICATED, selectedProfession],
    () => apiAuthenticatedService.getOffersByProfession(selectedProfession),
    { keepPreviousData: true }
  );

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError || !data) {
    return <div>An error occurred</div>;
  }

  const sortedOffers = [...data].sort(getComparator(order, orderBy));

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ padding: '0px' }}>
              <DisplayOfferInGridHeader
                Company={<></>}
                Location={
                  <TableSortLabel
                    active={orderBy === 'location'}
                    direction={orderBy === 'location' ? order : 'asc'}
                    onClick={() => handleSortRequest('location')}
                  >
                    Customer Location
                  </TableSortLabel>
                }
                Offer={
                  <>
                    |&nbsp;
                    <TableSortLabel
                      active={orderBy === 'offeringFixedCashAmount'}
                      direction={orderBy === 'offeringFixedCashAmount' ? order : 'asc'}
                      onClick={() => handleSortRequest('offeringFixedCashAmount')}
                    >
                      Offer
                    </TableSortLabel>
                    |
                  </>
                }
                ActionButton={<></>}
              />
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {sortedOffers.map((offer: OfferWithScope_UserWithoutId) => (
            <TableRow key={offer.id}>
              <TableCell colSpan={4}>
                <DisplayOfferInGridWrapper
                  offer={offer}
                  onActionButtonClick={() => onOpenDialog(offer)}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ReferralsAppMakeReferral;
